// ModuleSettings.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  Tag
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import Button, { IconButton } from '@components/common/Button';
import Modal, { ModalBody, ModalFooter } from '@components/common/Modal';
import Loading from '@components/common/Loading';

const ModuleSettings = () => {
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const queryClient = useQueryClient();

  // fetch modules
  const { data: modules, isLoading } = useQuery(
    ['modules-settings'],
    async () => {
      const res = await apiHelper.get('/modules');
      return res.data;
    },
    { staleTime: 30000 }
  );

  // modules mutation
  const modulesMutation = useMutation(
    async ({ action, data, id }) => {
      switch (action) {
        case 'create':
          return await apiHelper.post('/modules', data);
        case 'update':
          return await apiHelper.put(`/modules/${id}`, data);
        case 'delete':
          return await apiHelper.delete(`/modules/${id}`);
        default:
          throw new Error('invalid action');
      }
    },
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(['modules-settings']);
        toast.success(`Module ${vars.action}d successfully`);
        setShowModuleModal(false);
        setEditingItem(null);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Operation failed');
      }
    }
  );



  const handleCreateModule = () => {
    setEditingItem(null);
    setShowModuleModal(true);
  };

  const handleEditModule = (m) => {
    setEditingItem(m);
    setShowModuleModal(true);
  };


  // --- styles (CSS vars + inline) ---
  const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: 16 },
    card: {
      background: '#fff',
      borderRadius: 12,
      border: '1px solid rgba(14,20,25,0.04)',
      boxShadow: '0 10px 30px rgba(2,6,23,0.04)'
    },
    header: { padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
    headerLeft: { display: 'flex', gap: 12, alignItems: 'center' },
    iconBox: { width: 40, height: 40, borderRadius: 10, background: 'var(--primary-100, rgba(59,130,246,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    body: { padding: 0 },
    moduleRow: { padding: 16, borderBottom: '1px solid rgba(14,20,25,0.02)' },
    moduleHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
    moduleLeft: { display: 'flex', alignItems: 'center', gap: 12 },
    moduleMeta: { display: 'flex', gap: 12, alignItems: 'center' },
    categoryCard: { background: 'var(--gray-50,#f8fafc)', borderRadius: 10, padding: 12 },
    subcategoryRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 8, background: '#fff', borderRadius: 8, gap: 12 }
  };

  if (isLoading) {
    return (
      <div style={{ ...styles.card, padding: 24 }}>
        <Loading size="lg" text="Loading modules..." />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <motion.div style={{ ...styles.card }}>
        {/* header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconBox}>
              <Tag style={{ width: 18, height: 18, color: 'var(--primary-600)' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--gray-900)' }}>Module Management</h2>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--gray-600)' }}>
                                Organize your tickets with modules, categories and subcategories
              </p>
            </div>
          </div>

          <div>
            <Button variant="primary" leftIcon={<Plus style={{ width: 14, height: 14 }} />} onClick={handleCreateModule}>
                            Add Module
            </Button>
          </div>
        </div>

        {/* body: modules list */}
        <div style={styles.body}>
          {modules && modules.length > 0 ? (
            modules.map((module, mIndex) => (
              <motion.div
                key={module.id}
                style={styles.moduleRow}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18, delay: mIndex * 0.04 }}
              >
                <div style={styles.moduleHeaderRow}>
                  <div style={styles.moduleLeft}>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>{module.name}</div>
                      {module.description ? (
                        <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>{module.description}</div>
                      ) : null}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                    <IconButton variant="ghost" size="sm" onClick={() => handleEditModule(module)} tooltip="Edit module">
                      <Edit style={{ width: 14, height: 14 }} />
                    </IconButton>

                    <IconButton
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this module?')) {
                          modulesMutation.mutate({ action: 'delete', id: module.id });
                        }
                      }}
                      tooltip="Delete module"
                    >
                      <Trash2 style={{ width: 14, height: 14, color: 'var(--error-600)' }} />
                    </IconButton>
                  </div>
                </div>

              </motion.div>
            ))
          ) : (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Tag style={{ width: 48, height: 48, color: 'var(--gray-300)' }} />
              <h3 style={{ marginTop: 12, fontSize: 18, fontWeight: 700, color: 'var(--gray-900)' }}>No modules found</h3>
              <p style={{ color: 'var(--gray-600)' }}>Create your first module to start organizing tickets.</p>
              <div style={{ marginTop: 12 }}>
                <Button variant="primary" leftIcon={<Plus style={{ width: 14, height: 14 }} />} onClick={handleCreateModule}>Create Module</Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Module Modal */}
      <ModuleModal
        isOpen={showModuleModal}
        onClose={() => { setShowModuleModal(false); setEditingItem(null); }}
        module={editingItem}
        onSave={(data) => {
          const action = editingItem ? 'update' : 'create';
          const id = editingItem?.id;
          modulesMutation.mutate({ action, data, id });
        }}
        loading={modulesMutation.isLoading}
      />

    </div>
  );
};

/* ------------------ Module Modal ------------------ */
const ModuleModal = ({ isOpen, onClose, module, onSave, loading }) => {
  const [formData, setFormData] = useState({ name: '', description: '', is_active: true });

  useEffect(() => {
    if (module) setFormData({ name: module.name || '', description: module.description || '', is_active: module.is_active ?? true });
    else setFormData({ name: '', description: '', is_active: true });
  }, [module, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const inputStyle = (error) => ({
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: error ? '1px solid rgba(239,68,68,0.12)' : '1px solid rgba(14,20,25,0.04)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box'
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={module ? 'Edit Module' : 'Create Module'} size="md">
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'var(--gray-700)', fontSize: 13 }}>Module Name *</label>
              <input value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} style={inputStyle()} required />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 6, color: 'var(--gray-700)', fontSize: 13 }}>Description</label>
              <textarea value={formData.description} onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} rows={3} style={{ ...inputStyle(), minHeight: 80 }} />
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))} />
                <span style={{ fontSize: 14, color: 'var(--gray-700)' }}>Active</span>
              </label>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button type="submit" loading={loading}>{module ? 'Update' : 'Create'}</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};


export default ModuleSettings;
